import { jsx as _jsx } from "react/jsx-runtime";
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { useToggleTip } from '#pplx/frontend/aether/components/ToggleTip/useToggleTip.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
const toggleTipTriggerVariants = cva(
// Base classes (reset and interactable are provided by ButtonPrimitive)
'inline-flex cursor-pointer select-none items-center justify-center rounded-md text-secondary transition-colors duration-150 hover:bg-soft hover:text-primary', {
    variants: {
        size: {
            large: 'h-8 w-8',
            default: 'h-6 w-6',
            small: 'h-5 w-5',
        },
    },
    defaultVariants: {
        size: 'default',
    },
});
const getToggleTipTriggerClasses = (props) => {
    return classNames(toggleTipTriggerVariants(props), 
    // Open state styles
    'data-[state=open]:bg-soft data-[state=open]:text-primary');
};
/**
 * A toggle tip displays supplemental information via a trigger element.
 *
 * On desktop: opens on hover, clicking the trigger toggles the popover.
 * On mobile: clicking the trigger toggles the popover (no hover).
 *
 * Hover-opened popovers close when the pointer leaves both the trigger
 * and the content. Click-opened popovers stay open until explicitly
 * closed by another click or outside dismissal.
 *
 * @example
 * ```tsx
 * import { ToggleTip } from 'pplx/frontend/aether/components/ToggleTip';
 * import { IconInfoCircle } from '@tabler/icons-react';
 *
 * <ToggleTip
 *   content="This feature helps you organize your workspace more efficiently."
 *   aria-label="More information"
 * >
 *   <IconInfoCircle size={16} />
 * </ToggleTip>
 * ```
 */
export function ToggleTip({ ref, children, content, 'aria-label': ariaLabel, size = 'default', side = 'bottom', align = 'center', maxWidthPx = 320, offsetPx = 8, shouldShowArrow = false, disabled = false, open: controlledOpen, onOpenChange: controlledOnOpenChange, ...props }) {
    const targetRef = useMeasureableHandle(ref);
    const __radixProps = useRadixProps(props);
    const { isOpen, handlePointerEnter, handlePointerLeave, handleContentMouseEnter, handleContentMouseLeave, handleOpenChange, } = useToggleTip({
        open: controlledOpen,
        onOpenChange: controlledOnOpenChange,
    });
    const triggerElement = (_jsx(ButtonPrimitive, { ref: targetRef, ...__radixProps, className: getToggleTipTriggerClasses({ size }), "aria-label": ariaLabel, disabled: disabled, children: children }));
    if (disabled) {
        return triggerElement;
    }
    return (_jsx("span", { className: "inline-flex", onPointerEnter: handlePointerEnter, onPointerLeave: handlePointerLeave, children: _jsx(Popover, { triggerElement: triggerElement, appearance: "dark", side: side, align: align, maxWidthPx: maxWidthPx, offsetPx: offsetPx, shouldShowArrow: shouldShowArrow, disabled: disabled, shouldAutoFocus: false, open: isOpen, onOpenChange: handleOpenChange, onContentMouseEnter: handleContentMouseEnter, onContentMouseLeave: handleContentMouseLeave, children: content }) }));
}
//# sourceMappingURL=index.js.map